/* ═══════════════════════════════════════════════════════════════════════
   Typed API client — all backend calls centralised here
   ═══════════════════════════════════════════════════════════════════════ */

import type {
  CompanyListResponse,
  SummaryResponse,
  TrendResponse,
  ComparisonResponse,
  IngestRequest,
  IngestResponse,
  ConceptSearchResponse,
  ConceptCatalogResponse,
  AliasesResponse,
  TickerUniverseListResponse,
  QuoteResponse,
  PriceHistoryResponse,
  Company,
  FilingListResponse,
  InsiderListResponse,
  InsiderTradeListResponse,
  InstitutionalHolderListResponse,
  FundHoldingsResponse,
} from "../types";
import type { UserProfile } from "../store/auth";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${path}`);
  return res.json() as Promise<T>;
}

// ── Companies ────────────────────────────────────────────────────────

export function fetchCompanies() {
  return apiFetch<CompanyListResponse>("/api/companies");
}

// ── Insiders & Holders ───────────────────────────────────────────────

export function fetchInsiders(ticker: string) {
  return apiFetch<InsiderListResponse>(`/api/companies/${ticker}/insiders`);
}

export function fetchInsiderTrades(ticker: string) {
  return apiFetch<InsiderTradeListResponse>(
    `/api/companies/${ticker}/insider-trades`,
  );
}

export function fetchInstitutionalHolders(ticker: string) {
  return apiFetch<InstitutionalHolderListResponse>(
    `/api/companies/${ticker}/institutional-holders`,
  );
}

export function fetchFilings(ticker: string) {
  return apiFetch<FilingListResponse>(`/api/companies/${encodeURIComponent(ticker)}/filings`);
}

export function fetchFundHoldings(ticker: string, top = 50) {
  return apiFetch<FundHoldingsResponse>(
    `/api/companies/${encodeURIComponent(ticker)}/holdings?top=${top}`
  );
}

// ── Summary ──────────────────────────────────────────────────────────

export function fetchSummary(exchange: string, ticker: string) {
  return apiFetch<SummaryResponse>(`/api/financials/${exchange}/${ticker}/summary`);
}

// ── Trend ────────────────────────────────────────────────────────────

export function fetchTrend(
  exchange: string,
  ticker: string,
  concept: string,
  periods = 8,
  form?: string,
) {
  const params = new URLSearchParams({ periods: String(periods) });
  if (form) params.set("form", form);
  return apiFetch<TrendResponse>(
    `/api/financials/${exchange}/${ticker}/trend/${concept}?${params}`,
  );
}

// ── Compare ──────────────────────────────────────────────────────────

export function fetchCompare(
  exchange: string,
  concept: string,
  tickers: string[],
  periods = 1,
  form?: string,
) {
  const params = new URLSearchParams({
    tickers: tickers.join(","),
    periods: String(periods),
  });
  if (form) params.set("form", form);
  return apiFetch<ComparisonResponse>(
    `/api/financials/${exchange}/compare/${concept}?${params}`,
  );
}

// ── Ingestion ────────────────────────────────────────────────────────

export function ingestTickers(body: IngestRequest) {
  return apiFetch<IngestResponse>("/api/ingest", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Concepts (unused by frontend currently, included for completeness) ─

export function searchConcepts(keyword?: string, ticker?: string, limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (keyword) params.set("keyword", keyword);
  if (ticker) params.set("ticker", ticker);
  return apiFetch<ConceptSearchResponse>(
    `/api/financials/concepts/search?${params}`,
  );
}

export function fetchAliases() {
  return apiFetch<AliasesResponse>("/api/financials/concepts/aliases");
}

// ── Concept Catalog ──────────────────────────────────────────────────

export function fetchConceptCatalog(exchange: string, ticker: string, limit = 500) {
  const params = new URLSearchParams({ limit: String(limit) });
  return apiFetch<ConceptCatalogResponse>(
    `/api/financials/${exchange}/${ticker}/concepts?${params}`,
  );
}

// ── Re-ingest ────────────────────────────────────────────────────────

export function reingestTicker(ticker: string) {
  return apiFetch<IngestResponse>(`/api/ingest/reingest/${ticker}`, {
    method: "POST",
  });
}

// ── Ticker Universe ──────────────────────────────────────────────────

export function searchTickers(search: string, perPage = 10) {
  const params = new URLSearchParams({
    search,
    per_page: String(perPage),
  });
  return apiFetch<TickerUniverseListResponse>(`/api/tickers?${params}`);
}

// ── Quotes ───────────────────────────────────────────────────────────

export function fetchQuote(ticker: string) {
  return apiFetch<QuoteResponse>(`/api/quotes/${ticker}`);
}

// ── Price History ────────────────────────────────────────────────────

export function fetchPriceHistory(ticker: string, period = "1y") {
  return apiFetch<PriceHistoryResponse>(
    `/api/quotes/${encodeURIComponent(ticker)}/history?period=${encodeURIComponent(period)}`,
  );
}

// ── Peers ─────────────────────────────────────────────────────────────

export function fetchPeers(ticker: string) {
  return apiFetch<Company[]>(`/api/companies/${encodeURIComponent(ticker)}/peers`);
}

// ── Export (NotebookLM) ──────────────────────────────────────────────

export function fetchExportUrl(
  ticker: string,
  periods = 10,
  form = "10-K",
): string {
  const params = new URLSearchParams({
    periods: String(periods),
    form,
  });
  return `/api/export/${encodeURIComponent(ticker)}?${params}`;
}

// ── Auth ─────────────────────────────────────────────────────────────

export function verifyGoogleToken(access_token: string) {
  return apiFetch<UserProfile>("/api/auth/google", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token }),
  });
}

export function fetchCurrentUser() {
  return apiFetch<UserProfile>("/api/auth/me");
}

export function logout() {
  return apiFetch<{ status: string }>("/api/auth/logout", {
    method: "POST",
  });
}

// ── Preferences ──────────────────────────────────────────────────────

export function fetchPreferences() {
  return apiFetch<Record<string, any>>("/api/preferences");
}

export function savePreference(key: string, value: any) {
  return apiFetch<{ status: string; key: string }>(`/api/preferences/${key}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  });
}

export function deletePreference(key: string) {
  return apiFetch<{ status: string; key: string }>(`/api/preferences/${key}`, {
    method: "DELETE",
  });
}

// ── Templates ────────────────────────────────────────────────────────

import type { MetricTemplate } from "../hooks/useMetricTemplates";

export function fetchTemplates() {
  return apiFetch<MetricTemplate[]>("/api/templates");
}

export function createTemplate(template: Omit<MetricTemplate, "id" | "isGlobal">) {
  return apiFetch<MetricTemplate>("/api/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(template),
  });
}

export function deleteTemplate(templateId: string) {
  return apiFetch<{ status: string }>(`/api/templates/${templateId}`, {
    method: "DELETE",
  });
}

// ── Operations & Energy Grid ─────────────────────────────────────────

import type {
  OperationsSummaryResponse,
  OperationsGridResponse,
  OperationsEnvironmentalResponse,
  MacroGridResponse,
  GridSlippageResponse,
  BitemporalHistoryResponse,
  BessRteSeriesResponse,
  QueueVelocityResponse,
  FuelMixSeriesResponse,
} from "../types/operations";

export function fetchOperationsSummary(ticker: string, asOfSystemTime?: string) {
  const params = new URLSearchParams();
  if (asOfSystemTime) params.set("as_of_system_time", asOfSystemTime);
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<OperationsSummaryResponse>(`/api/v1/companies/${encodeURIComponent(ticker)}/operations${q}`);
}

export function fetchOperationsGrid(ticker: string, isoRto?: string, status?: string, asOfSystemTime?: string) {
  const params = new URLSearchParams();
  if (isoRto) params.set("iso_rto", isoRto);
  if (status) params.set("status", status);
  if (asOfSystemTime) params.set("as_of_system_time", asOfSystemTime);
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<OperationsGridResponse>(`/api/v1/companies/${encodeURIComponent(ticker)}/operations/grid${q}`);
}

export function fetchOperationsEnvironmental(ticker: string, statute?: string, violationsOnly = false) {
  const params = new URLSearchParams();
  if (statute) params.set("statute", statute);
  if (violationsOnly) params.set("violations_only", "true");
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<OperationsEnvironmentalResponse>(`/api/v1/companies/${encodeURIComponent(ticker)}/operations/environmental${q}`);
}

export function fetchMacroGrid(isoRto?: string, resourceType?: string, minCapacityMw?: number) {
  const params = new URLSearchParams();
  if (isoRto) params.set("iso_rto", isoRto);
  if (resourceType) params.set("resource_type", resourceType);
  if (minCapacityMw != null) params.set("min_capacity_mw", String(minCapacityMw));
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<MacroGridResponse>(`/api/v1/grid/macro${q}`);
}

export function fetchGridSlippage(minSlippageMonths = 3, isoRto?: string, limit = 50) {
  const params = new URLSearchParams({
    min_slippage_months: String(minSlippageMonths),
    limit: String(limit),
  });
  if (isoRto) params.set("iso_rto", isoRto);
  return apiFetch<GridSlippageResponse>(`/api/v1/grid/slippage?${params.toString()}`);
}

export function fetchBitemporalHistory(systemTime: string, entityType?: string, entityId?: string, isoRegion?: string) {
  const params = new URLSearchParams({ system_time: systemTime });
  if (entityType) params.set("entity_type", entityType);
  if (entityId) params.set("entity_id", entityId);
  if (isoRegion) params.set("iso", isoRegion);
  return apiFetch<BitemporalHistoryResponse>(`/api/v1/grid/bitemporal/history?${params.toString()}`);
}

export function fetchBessRteSeries(facilityId: number | string) {
  return apiFetch<BessRteSeriesResponse>(`/api/v1/grid/bess/${encodeURIComponent(String(facilityId))}/rte-series`);
}

export function fetchQueueVelocitySeries(
  iso: string,
  frequency = "quarterly",
  startYear = 2000,
  endYear = 2025,
  resourceType?: string,
) {
  const params = new URLSearchParams({
    frequency,
    start_year: String(startYear),
    end_year: String(endYear),
  });
  if (resourceType) params.set("resource_type", resourceType);
  return apiFetch<QueueVelocityResponse>(`/api/v1/grid/queues/${encodeURIComponent(iso)}/velocity-series?${params.toString()}`);
}

export function fetchRtoFuelMixSeries(
  ba: string,
  startDate?: string,
  endDate?: string,
  limit = 365,
) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  return apiFetch<FuelMixSeriesResponse>(`/api/v1/grid/rto/${encodeURIComponent(ba)}/fuel-mix-series?${params.toString()}`);
}

export { ApiError };



// ── Model Signals (gated) ────────────────────────────────────────────

export interface ModelSummary {
  model_name: string;
  prediction_count: number;
  latest_run_date: string | null;
}

export interface ModelPrediction {
  ticker: string;
  cik: string | null;
  run_date: string | null;
  signal_date: string | null;
  direction: string | null;
  score: number | null;
  decision: string | null;
  threshold: number | null;
  entry_price: number | null;
  target_price: number | null;
  stop_price: number | null;
  horizon_days: number | null;
  outcome_status: string | null;
  outcome_label: number | null;
  outcome_return_pct: number | null;
  features: Record<string, unknown> | null;
}

export interface ModelPredictionsResponse {
  model_name: string;
  run_date: string | null;
  spans_all_runs: boolean;
  count: number;
  predictions: ModelPrediction[];
}

export interface ModelPerformance {
  model_name: string;
  resolved_count: number;
  open_count: number;
  overall_hit_rate: number | null;
  // Candidate-only headline: performance over signals the model recommended.
  candidate_resolved_count: number;
  candidate_hit_rate: number | null;
  candidate_avg_return_pct: number | null;
  by_decision: Record<
    string,
    { count: number; wins: number; hit_rate: number | null; avg_return_pct: number | null }
  >;
}

export function fetchModelAccess() {
  return apiFetch<{ allowed: boolean }>("/api/v1/models/access");
}

export function fetchModels() {
  return apiFetch<{ models: ModelSummary[] }>("/api/v1/models");
}

export function fetchModelPredictions(
  modelName: string,
  decision?: string,
  outcome?: string,
) {
  const params = new URLSearchParams();
  if (decision) params.set("decision", decision);
  if (outcome) params.set("outcome", outcome);
  const qs = params.toString();
  return apiFetch<ModelPredictionsResponse>(
    `/api/v1/models/${encodeURIComponent(modelName)}/predictions${qs ? `?${qs}` : ""}`,
  );
}

export function fetchModelPerformance(modelName: string) {
  return apiFetch<ModelPerformance>(
    `/api/v1/models/${encodeURIComponent(modelName)}/performance`,
  );
}
